import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Paper, Box, Button, Alert } from "@mui/material";
import TableComponent from "../../components/Table/TableComponent.js";
import TableToolTip from "../../components/ToolTips/TableToolTip.js";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import * as styles from "../Wallet/Styles.js";
import IconComponent from "../../components/Icons/IconComponent.js";
import TitledSelector from "../../components/Selectors/TitledSelector.js";
import DateSelector from "../../components/Selectors/DateSelector.js";
import ApplyButton from "../../components/Buttons/ApplyButton.js";
import { mainBox } from "../../assets/Variables/SpaceVariables.js";
import { getLocalTimeZoneOffsetInSeconds, getTimestamps } from "../../Tools/DateTimeConvertions";
import { GetDashboardData, RemoveDashboardData } from "../../Tools/LocalStorage.js";
import WalletRequest from "../../Endpoints/WalletRequest.js";
import WalletTransactionsRequest from "../../Endpoints/WalletTransactionsRequest.js";
import LoadingSpinner from "../../components/Loaders/SpinLoader.js";
import { useCookies } from "../../Tools/CookieChecker.js";
import TitledMultiSelector from "../../components/Selectors/TitledMultiSelector.js";
import { BalanceFormatter, CurrencySymbol } from "../../components/CurrencyIcon/CurrencyIcon.js";
import TreeListRequest from "../../Endpoints/TreeListRequest.js";

const dateOptions = [
  { label: "Today", value: "Today" },
  { label: "This week", value: "This week" },
  { label: "This month", value: "This month" },
  { label: "This year", value: "This year" },
  { label: "Custom", value: "Custom" },
];

function ClientIndexPage() {
  const { name, type, currency, user } = useCookies();
  const [dateSelect, setDateSelect] = useState("Today");
  const [showDataPickers, setShowDataPickers] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [t] = useTranslation("global");
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState(rows);
  const [wallet, setWallet] = useState({});
  const [applyDisable, setApplyDisable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [walletApiMessage, setWalletApiMessage] = useState("");
  const [ibs, setIbs] = useState([{ label: "All", value: "All" }]);
  const [brokerSelect, setBrokerSelect] = useState([]);
  const [trees, setTrees] = useState([{ label: "All", value: "All" }]);
  const [treesSelect, setTreesSelect] = useState([]);
  const [transactionsType, setTransactionsType] = useState([
    { label: "Deposits", value: "DEPOSIT" },
    { label: "Withdrawals", value: "WITHDRAW" },
    { label: "Payouts", value: "PAYOUT" },
  ]);
  const [transactionsTypeSelect, setTransactionsTypeSelect] = useState(["DEPOSIT", "WITHDRAW", "PAYOUT"]);
  const [transactionsTypeVisible, setTransactionsTypeVisible] = useState(true);
  const [transactionSummary, setTransactionSummary] = useState(0);

  const RequestWalletData = async () => {
    try {
      const response = await WalletRequest();
      if (response.data != null) {
        setWallet(response.data.data.wallet)
      }
      else {
        setWallet({ id: "", currency: "", balance: "" })
      }
    }
    catch (error) {
      setWallet({ id: "", currency: "", balance: "" })
      console.log(error);
    }
  }

  const GetLocalStorageData = () => {
    let dataResponse = GetDashboardData();
    if (dataResponse.dataExits) {
      if (dataResponse.ibs.length > 0) {
        const formattedData = dataResponse.ibs.map((item) => ({
          label: item.name,
          value: item.email_ib,
        }));

        setIbs([{ label: "All", value: "All" }, ...formattedData]);
      }
      else {
        setIbs([]);
      }
    }
    else {
      setIbs([]);
    }
  }

  useEffect(() => {
    setRows([]);
    setFilteredRows([]);

    RequestWalletData();
    GetLocalStorageData();
    RequestTreeListData();

    let datetime = getTimestamps("Today");
    RequestTransactionWalletData({
      email: user || "",
      from: String(datetime.from),
      to: String(datetime.to),
    });
  }, []);

  useEffect(() => {
    const allValues = trees.map((item) => item.value);
    setTreesSelect(allValues);
  }, [trees]);

  const RequestTransactionWalletData = async (params) => {
    try {
      setApplyDisable(true);
      setLoading(true);
      const response = await WalletTransactionsRequest(params);
      if (response.data != null) {
        const payload = response.data.data || {};
        const message = response.data.message || "";
        const fallbackEmail = payload?.email || payload?.user?.email || user || "";
        const walletFromPayload = payload?.user?.wallet;
        const storedTransactions = Array.isArray(payload?.history)
          ? payload.history
          : Array.isArray(payload?.transactions)
            ? payload.transactions
            : [];
        setWalletApiMessage(message);

        console.log("[WalletIndexPage] wallet message:", message);
        console.log(
          "[WalletIndexPage] transactions count:",
          Array.isArray(storedTransactions) ? storedTransactions.length : 0
        );

        if (walletFromPayload) {
          setWallet(walletFromPayload);
        }

        if (
          storedTransactions &&
          Array.isArray(storedTransactions) &&
          storedTransactions.length > 0
        ) {
          let actualRows = storedTransactions.map((transaction, index) => {
            let tree_id = "";

            const normalizedType = String(transaction.transaction_type || "");
            const displayType = normalizedType
              ? normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1).toLowerCase()
              : "";
            if (displayType === 'Withdraw') {
              transaction.amount = -transaction.amount || 0.0
            }
            if (displayType === 'Payout') {
              tree_id = transaction.tree_id !== undefined && transaction.tree_id !== null ? transaction.tree_id : 0;
            }

            let row = {
              id: index + 1,
              date: transaction.date,
              broker_ib_email: transaction.broker_ib_email || fallbackEmail,
              type: displayType,
              amount: (transaction.amount || 0.0),
              allocated_volume: (transaction.allocated_volume || 0.0),
              currency: transaction.ib_currency ?? "ERR",
              comment: transaction.comment,
              ticket: transaction.trade_ticket,
              login: transaction.trade_login,
              platform: transaction.trading_server,
              tree_id: tree_id
            };
            return row;
          });
          setRows(actualRows);
          setFilteredRows(actualRows);

          if (actualRows.length > 0) {
            const summaryByCurrency = actualRows.reduce((acc, t) => {
              const currency = t.currency;

              if (!acc[currency]) {
                acc[currency] = 0;
              }

              acc[currency] += t.amount;

              return acc;
            }, {});

            const summaryText = Object.entries(summaryByCurrency).map(([currency, amount]) => `${CurrencySymbol.get(currency)} ${BalanceFormatter.round((amount || 0), currency)}`).join(" & ");
            setTransactionSummary(summaryText);
          }
          else {
            setTransactionSummary(0);
          }
        }
        else {
          setRows([]);
          setFilteredRows([]);
          setTransactionSummary(0);
        }
      }
    }
    catch (error) {
      console.log(error);
      const httpStatus = error?.response?.status ? `HTTP ${error.response.status}` : "HTTP N/A";
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        error?.message ||
        "Unknown error";
      setWalletApiMessage(`${httpStatus}: ${backendMessage}`);
      RemoveDashboardData();
    }
    setLoading(false);
    setApplyDisable(false);
  };

  const RequestTreeListData = async () => {
    try {
      const response = await TreeListRequest();
      if (response.data != null) {
        let storedTrees = response.data.data.trees;
        if (storedTrees && Array.isArray(storedTrees) && storedTrees.length > 0) {
          let actualTrees = storedTrees.map((tree, index) => {
            if (response.data.data.role === "IB") {
              const regex = /\(([^)]+)\)/;
              const coincidence = tree.description.match(regex);
              tree.description = coincidence ? coincidence[1] : tree.description
            }

            let row = {
              id: index,
              label: tree.id + " :: " + tree.description,
              value: tree.id,
            };
            return row;
          });

          setTrees([{ label: "All", value: "All" }, ...actualTrees]);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleBrokerIbSelector = (selectedValues) => {
    const allOptions = ibs.map((item) => item.value);

    const isAllSelected = selectedValues.includes("All");
    const hadAllBefore = brokerSelect.includes("All");

    if (isAllSelected && !hadAllBefore) {
      setBrokerSelect(allOptions);
      return;
    }

    if (!isAllSelected && hadAllBefore) {
      setBrokerSelect([]);
      return;
    }
    if (hadAllBefore && selectedValues.length < allOptions.length) {
      setBrokerSelect(selectedValues.filter((val) => val !== "All"));
      return;
    }

    const selectedWithoutAll = selectedValues.filter((val) => val !== "All");
    const allExceptAll = allOptions.filter((val) => val !== "All");

    const selectedAllManually =
      selectedWithoutAll.length === allExceptAll.length &&
      allExceptAll.every((val) => selectedWithoutAll.includes(val));

    if (selectedAllManually) {
      setBrokerSelect(allOptions);
      return;
    }

    setBrokerSelect(selectedValues);
  };

  const handleTreesSelector = (selectedValues) => {
    const allOptions = trees.map((item) => item.label);
    const allValues = trees.map((item) => item.value);

    const isAllSelected = selectedValues.includes("All");
    const hadAllBefore = treesSelect.includes("All");

    if (isAllSelected && !hadAllBefore) {
      setTreesSelect(allValues);
      return;
    }

    if (!isAllSelected && hadAllBefore) {
      setTreesSelect([]);
      return;
    }
    if (hadAllBefore && selectedValues.length < allOptions.length) {
      setTreesSelect(selectedValues.filter((val) => val !== "All"));
      return;
    }

    const selectedWithoutAll = selectedValues.filter((val) => val !== "All");
    const allExceptAll = allOptions.filter((val) => val !== "All");

    const selectedAllManually = selectedWithoutAll.length === allExceptAll.length;

    if (selectedAllManually) {
      setTreesSelect(allValues);
      return;
    }

    setTreesSelect(selectedValues);
  };

  const handleDownload = (dataArray) => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataArray);
    XLSX.utils.book_append_sheet(wb, ws, "ClientData");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fecha = new Date();
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    saveAs(blob, `${name}_wallet_${year}_${month}_${day}.xlsx`);
  };

  const handleDateSelector = (event) => {
    setDateSelect(event.target.value);
    if (event.target.value === "Custom") {
      setShowDataPickers(true);
    } else {
      setShowDataPickers(false);
    }
  };

  const handleFromChange = (date) => {
    setFromDate(date);
  };

  const handleToChange = (date) => {
    setToDate(date);
  };

  const handleTransactionType = (types) => {
    setTransactionsTypeSelect(types)
    if (types.includes("PAYOUT")) {
      setTransactionsTypeVisible(true)
    }
    else {
      setTransactionsTypeVisible(false)
    }

    if (types?.length > 0)
      setApplyDisable(false)
    else
      setApplyDisable(true)
  }

  const handleOnClick = () => {
    let selectedEmails = brokerSelect;
    if (brokerSelect.length > 0 && brokerSelect[0] === "All") {
      selectedEmails = brokerSelect.slice(1);
    }
    const emailToSend = selectedEmails?.[0] || user || "";

    let treesSide = [];
    if (treesSelect.length > 0 && !treesSelect.includes("All")) {
      treesSide = treesSelect;
    }

    if (dateSelect != "Custom") {
      setRows([]);
      setFilteredRows([]);
      let datetime = getTimestamps(dateSelect);

      RequestTransactionWalletData({
        email: emailToSend,
        from: String(datetime.from),
        to: String(datetime.to),
        tree_ids: transactionsTypeVisible ? treesSide : [],
        transaction_types: transactionsTypeSelect,
      });
    }
    else {
      if (fromDate != null || toDate != null) {
        setRows([]);
        setFilteredRows([]);
        RequestTransactionWalletData({
          email: emailToSend,
          from: String(fromDate / 1000 + getLocalTimeZoneOffsetInSeconds()),
          to: String(toDate / 1000 + getLocalTimeZoneOffsetInSeconds() + 86400),
          tree_ids: transactionsTypeVisible ? treesSide : [],
          transaction_types: transactionsTypeSelect,
        });
      }
    }
  };

  const columns = [
    {
      field: "id",
      flex: 0.45,
      minWidth: 70,
      headerClassName: "header_table",
      renderHeader: () => (
        <TableToolTip title="The date of creation of the account">
          <Box>{"ID "}</Box>
        </TableToolTip>
      ),
    },
    {
      field: "date",
      flex: 1.2,
      minWidth: 170,
      headerClassName: "header_table",
      renderHeader: () => <>{"Date"}</>,
    },
    ...(type === "BROKER" || type === "MASTER_IB"
      ? [
        {
          field: "broker_ib_email",
          flex: 1.35,
          minWidth: 200,
          headerClassName: "header_table",
          renderHeader: () => <>{"IB/Broker Email"}</>,
        },
      ]
      : []),
    {
      field: "type",
      flex: 0.7,
      minWidth: 90,
      headerClassName: "header_table",
      renderHeader: () => <>{"Type"}</>,
    },
    {
      field: "amount",
      flex: 0.8,
      minWidth: 110,
      headerClassName: "header_table",
      renderHeader: () => <>{`Amount`}</>,
      valueFormatter: (value) => {
        const [integer, decimal] = String(value || 0).split(".");
        return integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ") +
          (decimal ? `.${decimal}` : "");
      }
    },
    {
      field: "allocated_volume",
      flex: 0.8,
      minWidth: 70,
      headerClassName: "header_table",
      width: 100,
      renderHeader: () => <>{`Volume`}</>,
      renderCell: (params) => {
        const type = String(params?.row?.type ?? "").trim().toUpperCase();
        if (!type.includes("PAYOUT")) return "-";
        return params?.value || "-";
      },
    },
    {
      field: "currency",
      flex: 0.7,
      minWidth: 110,
      headerClassName: "header_table",
      renderHeader: () => <>{`Currency`}</>,
    },
    {
      field: "comment",
      flex: 1.9,
      minWidth: 260,
      headerClassName: "header_table",
      renderHeader: () => <>{"Comment"}</>,
    },
    {
      field: "ticket",
      flex: 0.9,
      minWidth: 120,
      headerClassName: "header_table",
      renderHeader: () => (
        <TableToolTip title="The payout structure established for this account.">
          <Box>
            {"Ticket "}
            <IconComponent icon="help" size="12px" />
          </Box>
        </TableToolTip>
      ),
    },
    {
      field: "login",
      flex: 0.8,
      minWidth: 110,
      headerClassName: "header_table",
      renderHeader: () => <>{"Login"}</>,
    },
    {
      field: "platform",
      flex: 1.1,
      minWidth: 150,
      headerClassName: "header_table",
      renderHeader: () => <>{"Platform"}</>,
    },
    {
      field: "tree_id",
      flex: 1,
      minWidth: 140,
      resizable: false,
      headerClassName: "header_table tree_header",
      renderHeader: () => <>{"Tree"}</>,
    },
  ];

  return (
    <Box sx={mainBox}>
      {loading ? <LoadingSpinner /> : <></>}
      <Paper elevation={2} sx={styles.MainPaper}>
        <Box sx={styles.TitleBox}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={styles.Title}
          >
            Wallet
          </Typography>
          <Button
            variant="outlined"
            sx={styles.ExportButton}
            onClick={() => handleDownload(filteredRows)}
            disabled={filteredRows.length == 0}
          >
            <IconComponent icon="download" size="12px" />
            &nbsp;Export
          </Button>
        </Box>
        <Box sx={styles.WalletInfoContainer}>
          <Typography sx={styles.WalletLabel1}>Balance:</Typography>
          <Typography sx={styles.WalletLabel2}>
            {CurrencySymbol.get(wallet.currency || currency)}{BalanceFormatter.round((wallet.balance || 0.0), (wallet.currency || currency))}
          </Typography>
        </Box>
        {walletApiMessage ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {walletApiMessage}
          </Alert>
        ) : null}
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
            }}
          >
            {
              type === "IB" || type === "MASTER_IB" ? (
                <>
                  <TitledMultiSelector
                    title="Transaction Type"
                    value={transactionsTypeSelect}
                    handleChange={handleTransactionType}
                    options={transactionsType}
                    tooltext="Select one or more transaction types. Using Payout you can also filter by trees."
                  />
                  {
                    transactionsTypeVisible &&
                    <TitledMultiSelector
                      title="Tree selector"
                      value={treesSelect}
                      handleChange={handleTreesSelector}
                      options={trees}
                      tooltext="Select one or more Trees linked to you to filter efficiently."
                    />
                  }
                </>)
                : (
                  null
                )
            }

            <TitledSelector
              title="Period of time"
              value={dateSelect}
              handleChange={handleDateSelector}
              options={dateOptions}
            />
            {showDataPickers && (
              <>
                <DateSelector
                  title="From"
                  value={fromDate}
                  handleChange={handleFromChange}
                />
                <DateSelector
                  title="To"
                  value={toDate}
                  handleChange={handleToChange}
                />
              </>
            )}

            {
              type === "BROKER" || type === "MASTER_IB" ? (
                <>
                  <TitledMultiSelector
                    title="IB selector"
                    value={brokerSelect}
                    handleChange={handleBrokerIbSelector}
                    options={ibs}
                    tooltext="Select a Broker or IB linked to you through trees (payout structures) to see their general performance overview."
                  />
                  <TableToolTip title="The search by IB can be performed individually or as a group. To view the broker's Wallet, deselect all options.">
                    <Box>
                      <ApplyButton HandleOnClick={handleOnClick} disable={applyDisable}>
                        Apply <IconComponent icon="help" size="12px" />
                      </ApplyButton>
                    </Box>
                  </TableToolTip>
                </>)
                : (
                  <ApplyButton HandleOnClick={handleOnClick} disable={applyDisable}>Apply</ApplyButton>
                )
            }
          </Box>
        </Box>
        <Box style={{ height: 600, width: "100%" }}>
          <TableComponent
            rows={filteredRows}
            columns={columns}
            TotalAmount={transactionSummary}
          />
        </Box>
      </Paper>
    </Box>
  );
}

export default ClientIndexPage;
